import { RegExp } from "./RegExp";
import { distinct } from "./Array";
import Url from "./Url";

/**
 * Draw html string into a canvas
 * @param {HTMLElement | String} html html string that want to draw into a canvas
 *
 * @return {Promise} resolve a canvas
 */
export async function drawHtml(html, canvas) {
  // Create canvas
  if (!(canvas instanceof window.HTMLCanvasElement)) {
    canvas = document.createElement("canvas");
  }
  const ctx = canvas.getContext("2d");

  // Create data url from html
  const htmlText = html instanceof HTMLElement ? html.outerHTML : html;
  const url = await createImageUrlFromHtml(htmlText);

  // Load data via Image object
  const img = await downloadImage(url, { type: "image" });

  // Draw image into canvas
  ctx.drawImage(img, 0, 0);

  // Revoke URL to optimize memory
  revokeUrl(url);

  return canvas;
}

export function revokeUrl(url) {
  const DOMURL = window.URL || window.webkitURL || window;
  DOMURL.revokeObjectURL(url);
}

/**
 * Draw svg string into canvas
 *
 * @param {String} svg
 */
export async function drawSvg(svg, canvas) {
  // Create canvas
  if (!(canvas instanceof window.HTMLCanvasElement)) {
    canvas = document.createElement("canvas");
  }

  const ctx = canvas.getContext("2d");

  // Create url from svg string
  const data = new Blob([svg], { type: "image/svg+xml" });
  // Using createObjectURL will cause error: Tainted canvases may not be exported
  // const DOMURL = window.URL || window.webkitURL || window
  // const url = DOMURL.createObjectURL(data)
  const url = buildSvgImageUrl(data);

  // Load Image
  const img = await downloadImage(url, { type: "image" });

  // Draw image into canvas
  ctx.drawImage(img, 0, 0);

  // Revoke URL to optimize memory
  // DOMURL.revokeObjectURL(url)

  return canvas;
}

/**
 * Create image url from html string
 * NOTE: Url will need to be revoke to optimize memory resource
 *
 * @param {String} html html string
 * @param {Object} options specify svg size { number width?, number height? }
 *
 * @return {String} image url
 */
export async function createImageUrlFromHtml(html, options) {
  // Get Template
  let data = getSvgTemplate(html, options);

  // Replace image urls by base64 string
  data = await replaceImageUrlsByBase64(data);

  // Create url
  // const svg = new Blob([data], { type: 'image/svg+xml;charset=utf-8' })
  // Using createObjectURL will cause error: Tainted canvases may not be exported
  // const DOMURL = window.URL || window.webkitURL || window
  // const url = DOMURL.createObjectURL(svg)

  const url = buildSvgImageUrl(data);

  return url;
}

export function buildSvgImageUrl(svg) {
  const b64 = window.btoa(svg);
  return "data:image/svg+xml;base64," + b64;
}

/**
 * Svg template that wrap html string into a <foreignObject></foreignObject> tag that can be convert to Blob
 *
 * @param {String} html html string
 * @param {Object} options specify svg size { number width?, number height? }
 *
 * @return {String} svg string
 */
export function getSvgTemplate(html, options) {
  const width = (options && options.width) || 230;
  const height = (options && options.height) || 252;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><foreignObject width="100%" height="100%"><div xmlns="http://www.w3.org/1999/xhtml">${html}</div></foreignObject></svg>`;
}

export function parseHTML(aHTMLString) {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(aHTMLString, "text/xml");
  return xmlDoc;
}

/**
 * Replace image urls by base64 string in html
 *
 * @param {String} html html string
 *
 * @return {Promise} resolve html string
 */
export async function replaceImageUrlsByBase64(html) {
  // get urls
  const urls = getImageUrls(html);

  // load urls as base64
  const imgData = await loadImageUrls(urls, { type: "dataurl" });

  // replace urls by base64
  imgData.forEach(img => {
    // Because urls was distincted, so it must use regexp to replace all urls in html string
    html = html.replace(new window.RegExp(img.url, "g"), img.data);
  });

  return html;
}

/**
 * Search image urls in html string
 *
 * @param {String} html html string
 *
 * @return {Array} urls
 */
export function getImageUrls(html) {
  let urls = RegExp.urlImgTag(html).map(g => g[1]);
  urls = distinct(urls);
  return urls;
}

/**
 * Load image url as type in options
 *
 * @param {Array} urls image urls
 * @param {Object} options download options. { string type - return image as type data: 'image', 'dataurl', 'blob', string format? - image mime, number quality? - 0..1 }
 *
 * @return {Promise} resolve [...{ string url, Object data }]
 */
export async function loadImageUrls(urls, options = { type: "dataurl" }) {
  // Make requests
  let requests = urls.map(url => downloadImage(url, options));

  // Request all
  const imgs = await Promise.all(requests);

  // Bind url with it's data
  const imgData = urls.map((url, index) => ({ url: url, data: imgs[index] }));

  return imgData;
}

/**
 * Download image data from url
 *
 * @param {String} url
 * @param {Object} [options]
 * @param {String} [options.type='dataurl'] 'image', 'dataurl', 'blob'
 * @param {String} [options.format='image/png'] mime type
 * @param {Number} [options.quality=0.92] 0..1
 * @param {String} [options.crossOrigin='anonymous']
 * @param {Number} [options.width]
 * @param {Number} [options.height]
 *
 * @return {Promise.<Image|Blob|String>} resolve image data as type in options.type
 */
export function downloadImage(url, options = {}) {
  // Get Options
  const type = options.type || "dataurl";
  const format = options.format || "image/png";
  const quality = options.quality || 0.92;
  const crossOrigin = options.crossOrigin || "anonymous";

  // Return promise
  return new Promise((resolve, reject) => {
    let img =
      isNaN(options.width) && isNaN(options.height)
        ? new Image()
        : new Image(options.width, options.height);
    img.setAttribute("crossOrigin", crossOrigin);
    img.setAttribute("alt", "");

    img.onload = () => {
      // Only need image
      if (type === "image") {
        resolve(img);
        return;
      }

      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.height = img.height;
        canvas.width = img.width;

        ctx.drawImage(img, 0, 0);

        if (type === "dataurl") {
          const imgDataUrl = canvas.toDataURL(format, quality);
          resolve(imgDataUrl);
          return;
        }

        if (type === "blob") {
          canvas.toBlob(
            blob => {
              resolve(blob);
            },
            format,
            quality
          );
          return;
        }

        throw new Error("Does not support type " + type);
      } catch (error) {
        console.error(error);
        reject(error);
      }
    };

    img.onerror = err => reject(err);

    img.src = Url.validate(url) ? url.replace(/&amp;/g, "&") : url;

    // make sure the load event fires for cached images too
    if (img.complete || img.complete === undefined) {
      img.src =
        "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";
      img.src = url;
    }
  });
}
